import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "./api";

/**
 * useVisitBeacon — mounts once at the SPA root and fires a
 * privacy-safe POST /api/track/visit whenever the pathname changes.
 *
 * Data shipped:
 *   - `visit_id`: opaque random id, per-browser, stored in localStorage
 *                 (NOT a cookie -- this stays first-party only)
 *   - `path`:     the raw pathname (server buckets it to a coarse label
 *                 like '/shop' or '/product' before storing).
 *
 * Never sent: IP (server would infer it anyway but we don't store it),
 *             email, phone, name, referrer, or user-agent.
 *
 * Admin routes are excluded — internal traffic should not inflate the
 * customer funnel.
 */
const VISIT_ID_KEY = "mps_visit_id_v1";

function getOrCreateVisitId() {
  let id = null;
  try {
    id = localStorage.getItem(VISIT_ID_KEY);
    if (!id) {
      id = "v-" + (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
      localStorage.setItem(VISIT_ID_KEY, id);
    }
  } catch { id = "v-transient-" + Math.random().toString(36).slice(2); }
  return id;
}

export function useVisitBeacon() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin")) return;               // exclude staff traffic
    if (typeof window === "undefined") return;
    const visit_id = getOrCreateVisitId();
    // fire-and-forget; the beacon must NEVER block rendering or throw
    api.post("/track/visit", { visit_id, path: pathname }).catch(() => {});
  }, [pathname]);
}
